import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap, cx } from '@atlaskit/css';
import { useResizingHeight } from '@atlaskit/motion';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { AudioFeatureType } from '../../../api/cc-smarts/models/AudioFeature';
import type { TrackType } from '../../../api/cc-smarts/models/TrackType';
import { useAudioPlayerStreamingContext } from '../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../hooks/useAudioPlayerStreamingStore';
import { useContextMenuActions } from '../../hooks/useContextMenuActions';
import {
	resetPlaylistDefaultExpanded,
	setActiveContent,
	setPlaylistHasUnavailableContent,
} from '../../state/AudioPlayerState';
import { AudioStreamingState } from '../../state/AudioPlayerStreamingStore';
import { MinifiedAudioPlayer } from '../audio-player/MinifiedAudioPlayer';

import type { PlaylistQuery as PlaylistQueryType } from './__generated__/PlaylistQuery.graphql';
import { FooterMessage } from './FooterMessage';
import { PlaylistEmptyView } from './PlaylistEmptyView';
import { PlaylistErrorView } from './PlaylistErrorView';
import { PlaylistHeader } from './PlaylistHeader';
import { type PlaylistItemData } from './PlaylistItem';
import { PlaylistLoadingView } from './PlaylistLoadingView';
import { PlaylistView } from './PlaylistView';

type UnavailableItem = {
	mainSource: string;
	supportingSources: string[];
	trackType: string;
};

const styles = cssMap({
	wrapper: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overflow'),
		position: 'absolute',
		bottom: token('space.100'),
		left: token('space.100'),
		right: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	contentContainer: {
		height: '406px',
		position: 'relative',
		overflowY: 'auto',
	},
	contentContainerWithPendingContent: {
		height: '468px',
	},
	fullHeightWrapper: {
		flexGrow: 1,
	},
	playlistInnerContainer: {
		flex: 1,
		minHeight: '0px',
		position: 'relative',
		height: '100%',
	},
	coverBorder: {
		position: 'absolute',
		width: '100%',
		top: '0',
		backgroundColor: token('elevation.surface'),
		zIndex: 200,
		height: '12px',
	},
	stickyShadow: {
		position: 'sticky',
		top: token('space.negative.050'),
		zIndex: 1,
		boxShadow: token('elevation.shadow.overflow'),
		backgroundColor: token('elevation.surface'),
		height: '5px',
	},
	minifiedAudioPlayer: {
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
	},
});

type PlaylistWrapperProps = {
	children: React.ReactNode;
	defaultExpanded?: boolean;
	hasOtherActivePlayback?: boolean;
};

const PlaylistWrapper = ({
	children,
	defaultExpanded = false,
	hasOtherActivePlayback = false,
}: PlaylistWrapperProps) => {
	const resizingHeightProps = useResizingHeight();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const { activeContent, clearPendingContent } = useAudioPlayerStore();

	useEffect(() => {
		setIsExpanded(defaultExpanded);
	}, [defaultExpanded]);

	const handleToggle = () => {
		setIsExpanded((prev: boolean) => {
			// Reset the unavailable content flag
			setPlaylistHasUnavailableContent(false);
			return !prev;
		});
		resetPlaylistDefaultExpanded();
	};

	const handleMinifiedPlayerClick = () => {
		clearPendingContent();
	};

	const shouldShowMinifiedPlayer = !isExpanded && hasOtherActivePlayback;

	return (
		<Flex direction="column" xcss={styles.wrapper}>
			<PlaylistHeader isExpanded={isExpanded} onToggle={handleToggle} />
			{shouldShowMinifiedPlayer && (
				<Box xcss={styles.minifiedAudioPlayer}>
					<MinifiedAudioPlayer
						title={activeContent.title || ''}
						byline=""
						avatarUrl={activeContent.avatarUrl || ''}
						elevated
						onClick={handleMinifiedPlayerClick}
					/>
				</Box>
			)}
			<div {...resizingHeightProps}>
				{isExpanded && (
					<Flex
						xcss={cx(
							styles.contentContainer,
							hasOtherActivePlayback && styles.contentContainerWithPendingContent,
						)}
						direction="column"
					>
						{/* Scroll shadow effect */}
						<Box xcss={styles.coverBorder} />
						<Flex direction="column" xcss={styles.fullHeightWrapper}>
							<Box xcss={styles.stickyShadow} />
							{children}
						</Flex>
					</Flex>
				)}
			</div>
		</Flex>
	);
};

type PlaylistInnerProps = {
	cloudId: string;
	fetchKey: number;
};

const PlaylistInner = ({ cloudId, fetchKey }: PlaylistInnerProps) => {
	const { fetchAudio } = useAudioPlayerStreamingContext();
	const { activeContent, playlistHasUnavailableContent } = useAudioPlayerStore();
	const { removeTrack, addTrack } = useContextMenuActions(cloudId);

	const data = useLazyLoadQuery<PlaylistQueryType>(
		graphql`
			query PlaylistQuery($cloudId: ID!) {
				confluence_getPlaylist(cloudId: $cloudId) {
					tracks {
						nodes {
							id
							type
							mainSource
							mainContent {
								id
								title
								type
								status
								subType
								space {
									key
								}
								history {
									createdBy {
										displayName
										profilePicture {
											path
										}
									}
								}
								links {
									base
									webui
								}
								properties(keys: ["emoji-title-published"]) {
									nodes {
										value
									}
								}
							}
							supportingSources
							supportingContent {
								ari
								links {
									base
									webui
								}
							}
						}
					}
				}
			}
		`,
		{ cloudId },
		{
			fetchPolicy: 'network-only',
			fetchKey,
		},
	);

	const { playlistItems: serverPlaylistItems, unavailableItems } = useMemo(() => {
		const tracks = (data as any).confluence_getPlaylist?.tracks?.nodes || [];
		const filtered: PlaylistItemData[] = [];
		const unavailable: UnavailableItem[] = [];

		tracks.forEach((track: any) => {
			const mainContent = track.mainContent;
			const supportingContent = track.supportingContent;
			const hasMainContent = !!mainContent;
			const hasValidStatus = mainContent?.status === 'current' || mainContent?.status === 'draft';

			if (hasMainContent && hasValidStatus) {
				const creator = mainContent.history?.createdBy;
				const avatarUrl = creator?.profilePicture?.path || undefined;
				const item: PlaylistItemData = {
					id: track.id,
					title: mainContent.title || undefined,
					trackType: track.type,
					author: creator?.displayName || undefined,
					avatarUrl,
					spaceKey: mainContent.space?.key || '',
					contentType: mainContent.type || '',
					isLive: !!(mainContent.subType === 'live'),
					emojiId: mainContent.properties?.nodes?.[0]?.value || undefined,
					contentId: mainContent.id,
					mainContent: {
						ari: track.mainSource,
						link: mainContent.links?.base + mainContent.links?.webui || '',
					},
					supportingContent: supportingContent.map((content: any) => ({
						ari: content.ari,
						link: content.links?.base + content.links?.webui || '',
					})),
				};
				filtered.push(item);
			} else {
				const unavailableItem: UnavailableItem = {
					mainSource: track.mainSource,
					supportingSources: track.supportingSources || [],
					trackType: track.type,
				};
				unavailable.push(unavailableItem);
			}
		});

		return { playlistItems: filtered, unavailableItems: unavailable };
	}, [data]);

	// Remove unavailable items from the playlist and show unavailable content notification
	useEffect(() => {
		if (unavailableItems.length === 0) {
			return;
		}
		setPlaylistHasUnavailableContent(true);
		Promise.allSettled(
			unavailableItems.map((item) =>
				removeTrack(item.mainSource, item.supportingSources, item.trackType as TrackType),
			),
		);
	}, [unavailableItems, removeTrack]);

	const shouldShowFooterMessage = playlistHasUnavailableContent;

	const currentPlaylistItems = serverPlaylistItems;

	const { state: streamingState } = useAudioPlayerStreamingStore();
	const isUninitialized = streamingState.state === AudioStreamingState.UNINITIALIZED;
	const { activeFeature, setActiveFeature } = useAudioPlayerStore();
	const currentItem = useMemo(() => {
		if (!activeContent?.contentId || isUninitialized) {
			return null;
		}
		return (
			currentPlaylistItems.find(
				(item) =>
					item.contentId === activeContent.contentId &&
					item.trackType === activeFeature.toString().toUpperCase(),
			) || null
		);
	}, [activeContent?.contentId, currentPlaylistItems, isUninitialized, activeFeature]);

	const handleItemClick = useCallback(
		(item: PlaylistItemData) => {
			const supportingSources = item.supportingContent.map((content) => content.ari);
			// Add the track to the top of the playlist
			addTrack(item.mainContent.ari, supportingSources, item.trackType, {
				top: true,
				showFlags: false,
			}).catch(() => {
				// Silently handle errors - the track will still play even if add fails
				// TODO: analytics
			});
			setActiveContent({
				contentId: item.contentId,
				spaceKey: item.spaceKey || '',
				contentType: item.contentType || '',
				title: item.title,
				author: item.author,
				avatarUrl: item.avatarUrl || '',
				supportingContent: item.supportingContent,
				mainContentLink: item.mainContent.link,
			});
			setActiveFeature(item.trackType.toLowerCase() as AudioFeatureType);
			fetchAudio();
		},
		[addTrack, fetchAudio, setActiveFeature],
	);

	return (
		<Flex direction="column" xcss={styles.playlistInnerContainer}>
			{currentPlaylistItems.length === 0 ? (
				<PlaylistEmptyView />
			) : (
				<PlaylistView
					items={currentPlaylistItems}
					currentItem={currentItem}
					onItemClick={handleItemClick}
				/>
			)}
			{shouldShowFooterMessage && (
				<FooterMessage onClose={() => setPlaylistHasUnavailableContent(false)} />
			)}
		</Flex>
	);
};

type PlaylistProps = {
	cloudId: string;
	defaultExpanded?: boolean;
};

export const Playlist = ({ cloudId, defaultExpanded }: PlaylistProps): React.JSX.Element => {
	const { pendingContent } = useAudioPlayerStore();
	const { state: streamingState } = useAudioPlayerStreamingStore();
	const isUninitialized = streamingState.state === AudioStreamingState.UNINITIALIZED;

	const [fetchKey, setFetchKey] = useState(0);

	const handleIncrementFetchKey = useCallback(() => {
		setFetchKey((prev) => prev + 1);
	}, []);

	return (
		<PlaylistWrapper
			defaultExpanded={defaultExpanded}
			hasOtherActivePlayback={pendingContent !== null && !isUninitialized}
		>
			<ErrorBoundary
				key={fetchKey}
				fallbackRender={(props) => (
					<PlaylistErrorView {...props} onRetry={handleIncrementFetchKey} />
				)}
			>
				<Suspense fallback={<PlaylistLoadingView />}>
					<PlaylistInner cloudId={cloudId} fetchKey={fetchKey} />
				</Suspense>
			</ErrorBoundary>
		</PlaylistWrapper>
	);
};
